import { Col, Row } from 'antd';
import { LoadingContainer } from 'app/components/LoadingContainer/Loadable';
import StyledButton from 'app/components/StyledButton';
import { PaymentSuccess } from 'app/pages/NewPaymentCallback/components/Loadable';
import { PaymentFail } from 'app/pages/NewPaymentCallback/components/PaymentFail';
import {
  selectInfoUser,
  selectLanguage,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import { getReservationBookingDetailApi } from 'app/services/reservationHistoryApi';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import useWindowSize from 'app/hooks/useWindowSize';
import { PaymentCallbackWrapper } from './styled';
import { DEFAULT_RESTAURANT_IMAGE } from '../constants';
import { downloadImageQR, generateQR } from '../qrUtils';
import { ReservationHistory_PaymentStatus } from '@/app/pages/reservation-history/store';
import { format, isValid, parseISO } from 'date-fns';
import { enUS, vi } from 'date-fns/locale';
import { formatDateBEtoUTCFormat } from '@/lib/utils';

interface Props {}

const mapReservationDetailToCallbackOrder = (
  detail: any,
  reservationNo: string,
) =>
  ({
    ...detail,
    id: detail?.reservationNo ?? reservationNo,
    idHash: detail?.reservationNo ?? reservationNo,
    orderState: {
      value:
        `${detail?.status ?? ''}`.toUpperCase() === 'CONFIRMED' ||
        `${detail?.paymentStatus ?? ''}`.toUpperCase() === 'PAID'
          ? 'success'
          : detail?.orderState?.value ?? 'paymentPending',
    },
  } as any);

const getDateFnsLocale = (language?: string) => (language === 'vi' ? vi : enUS);

const normalizeUsername = (value?: string) =>
  `${value ?? ''}`.trim().toLowerCase();

export const formatUsageTime = (detail: any, language?: string) => {
  const date = detail?.reservationDate;
  const time = detail?.reservationTime;

  if (!date) return time ?? '-';

  const parsedDate = parseISO(formatDateBEtoUTCFormat(date));
  const dateLabel = isValid(parsedDate)
    ? format(parsedDate, 'EEEE, dd/MM/yyyy', {
        locale: getDateFnsLocale(language),
      })
    : date;

  return [dateLabel, time].filter(Boolean).join(', ');
};

const ReservationInfoCard = ({
  detailOrder,
  language,
  t,
  onDownloadQR,
  qrImage,
  canShowQR,
}: {
  detailOrder: any;
  language?: string;
  t: (key: string, fallback: string) => string;
  onDownloadQR: () => void;
  qrImage: string;
  canShowQR: boolean;
}) => (
  <div className="mx-auto mb-6 w-[490px] max-w-full rounded-xl bg-[#fff4e8] p-3.5 text-left">
    <div className="flex gap-5 max-[540px]:gap-3">
      {canShowQR && (
        <div className="w-[116px] flex-[0_0_116px]">
          <img
            className="h-[112px] w-[112px] bg-white object-contain"
            src={qrImage || detailOrder.qrImage}
            alt=""
          />
          <button
            type="button"
            className="mt-1 border-0 bg-transparent p-0 text-left text-[11px] leading-4 text-[#f8a515]"
            onClick={onDownloadQR}
          >
            {t('b2b.restaurantHistory.downloadQr', 'Tải QR nhận bàn')}
          </button>
        </div>
      )}
      <div className="flex min-h-[112px] min-w-0 flex-1 flex-col justify-center pb-1">
        <img
          className="mb-2 h-auto max-h-7 w-[74px] object-contain object-left"
          src={detailOrder.logo || DEFAULT_RESTAURANT_IMAGE}
          alt=""
        />
        <div className="text-xs font-normal leading-[18px]">
          {t('b2b.txt.restaurant', 'Nhà hàng')}
        </div>
        <div className="mt-0.5 text-sm font-bold leading-5">
          {detailOrder?.storeName || '-'}
        </div>
        <div className="mt-1.5 text-xs leading-[18px] text-[#777]">
          {t('b2b.restaurantHistory.reservationCode', 'Mã đặt bàn')}:{' '}
          {detailOrder?.reservationNo || '-'}
        </div>
      </div>
    </div>
    <div className="mt-3 grid grid-cols-[120px_1fr] items-start gap-x-2 gap-y-1">
      <div className="text-[13px] font-normal leading-5">
        {t(
          'app_native_booking.loyalty.voucherDetail.usageTime',
          'Thời gian sử dụng',
        )}
        :
      </div>
      <div className="text-[13px] font-bold leading-5">
        {formatUsageTime(detailOrder, language)}
      </div>
      <div className="text-[13px] font-normal leading-5">
        {t('b2b.restaurantHistory.guestAmount', 'Số lượng khách')}:
      </div>
      <div className="text-[13px] font-bold leading-5">
        {detailOrder?.partySize || '-'}
      </div>
      <div className="text-[13px] font-normal leading-5">
        {t('restaurant.booking.zone', 'Khu vực')}:
      </div>
      <div className="text-[13px] font-bold leading-5">
        {detailOrder?.zoneName || '-'}
      </div>
      <div className="text-[13px] font-normal leading-5">
        {t('b2b.restaurantHistory.specialRequest', 'Yêu cầu')}:
      </div>
      <div className="text-[13px] font-bold leading-5">
        {detailOrder?.note || '-'}
      </div>
      {detailOrder?.paymentStatus ===
        ReservationHistory_PaymentStatus.COMPLETED && (
        <>
          <div className="text-xs font-normal leading-[18px]">
            {t('b2b.txt.status', 'Trạng thái')}:
          </div>
          <div className="text-[13px] font-semibold leading-[18px] text-[#16a34a]">
            {t('b2b.restaurantHistory.depositPaidPrefix', 'Đã đặt cọc')}
          </div>
        </>
      )}
    </div>
  </div>
);

export const RestaurantPaymentCallback = memo((props: Props) => {
  const location = useLocation();
  const history = useHistory();
  const translations = useSelector(selectTranslations);
  const language = useSelector(selectLanguage);
  const infoUser = useSelector(selectInfoUser);
  const t = (key: string, fallback: string) => translations?.[key] ?? fallback;
  const { device } = useWindowSize();

  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(true);
  const [detailOrder, setDetailOrder] = useState<any>();
  const [qrImage, setQrImage] = useState('');

  const onGoHistory = () => history.push('/reservation-history');
  const qrReservationNo = detailOrder?.reservationNo || detailOrder?.idHash;
  const canShowReservationQr =
    normalizeUsername(infoUser?.username) ===
    normalizeUsername(detailOrder?.agent?.username);
  const hasReservationInfo = Boolean(
    detailOrder?.storeName ||
      detailOrder?.reservationNo ||
      detailOrder?.reservationTime ||
      detailOrder?.partySize ||
      detailOrder?.zoneName ||
      detailOrder?.note,
  );

  useEffect(() => {
    if (!isSuccess || !qrReservationNo || !canShowReservationQr) {
      setQrImage('');
      return;
    }

    let isMounted = true;

    generateQR(qrReservationNo)
      .then(dataUrl => {
        if (isMounted) setQrImage(dataUrl);
      })
      .catch(() => {
        if (isMounted) setQrImage('');
      });

    return () => {
      isMounted = false;
    };
  }, [canShowReservationQr, isSuccess, qrReservationNo]);

  const handleDownloadQR = useCallback(() => {
    if (!qrReservationNo || !canShowReservationQr) return;

    downloadImageQR({
      reservationNo: qrReservationNo,
      restaurantName: detailOrder?.storeName,
    });
  }, [canShowReservationQr, detailOrder?.storeName, qrReservationNo]);

  useEffect(() => {
    const fetchDetail = async () => {
      const search = new URLSearchParams(location.search);
      const reservationNo =
        search.get('reservationNo') ||
        search.get('vnp_TxnRef') ||
        search.get('orderId') ||
        '';
      const resultCode = search.get('result_code');
      const responseCode = search.get('vnp_ResponseCode');
      const success =
        resultCode !== null ? resultCode === '1' : responseCode === '00';

      setIsSuccess(success);
      if (!reservationNo) return;

      try {
        setLoading(true);
        const { data } = await getReservationBookingDetailApi({
          reservationNo,
        });
        setDetailOrder(
          mapReservationDetailToCallbackOrder(data, reservationNo),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [location.search]);

  return (
    <LoadingContainer isFullPage loading={loading}>
      {detailOrder && (
        <>
          <Row justify="center">
            <Col span={device === 'desktop' ? 12 : 22}>
              {isSuccess !== null && (
                <PaymentCallbackWrapper id="pay" className="main">
                  {isSuccess ? (
                    <>
                      <PaymentSuccess
                        detailOrder={detailOrder}
                        titleText={t(
                          'b2b.restaurantHistory.result.success.title',
                          'Đặt bàn thành công',
                        )}
                        orderNumberText={t(
                          'b2b.restaurantHistory.reservationCode',
                          'Mã đặt bàn',
                        )}
                        contentText={t(
                          'b2b.restaurantHistory.result.success.content',
                          'Chúng tôi sẽ gửi thông tin đặt bàn đến Email của bạn. Vui lòng kiểm tra Email.',
                        )}
                        afterOrderNumberContent={
                          hasReservationInfo ? (
                            <ReservationInfoCard
                              detailOrder={detailOrder}
                              language={language}
                              t={t}
                              onDownloadQR={handleDownloadQR}
                              qrImage={qrImage}
                              canShowQR={canShowReservationQr}
                            />
                          ) : undefined
                        }
                      />
                    </>
                  ) : (
                    <PaymentFail
                      detailOrder={detailOrder}
                      titleText={t(
                        'b2b.restaurantHistory.result.cancelled.title',
                        'Hủy đặt bàn',
                      )}
                      orderNumberText={t(
                        'b2b.restaurantHistory.reservationCode',
                        'Mã đặt bàn',
                      )}
                      contentText={t(
                        'b2b.restaurantPaymentCallback.fail.content',
                        'Đơn đặt bàn chưa được thanh toán thành công.',
                      )}
                      subContentText={t(
                        'b2b.restaurantPaymentCallback.fail.subContent',
                        'Vui lòng kiểm tra lại thông tin đặt bàn hoặc thử thanh toán lại.',
                      )}
                    />
                  )}
                </PaymentCallbackWrapper>
              )}
            </Col>
          </Row>
          <Row style={{ marginTop: 24 }}>
            <Col
              span={24}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <StyledButton
                textColor="#808080"
                bgColor="white"
                style={{
                  width: 'auto',
                  fontSize: 16,
                  fontWeight: 600,
                  borderRadius: 5,
                }}
                border="1px solid #808080"
                onClick={onGoHistory}
              >
                <i className="fa-solid fa-arrow-left"></i>
                <span className="--textBtn" style={{ marginLeft: 8 }}>
                  {translations?.['b2b.txt.back-to-history'] ?? ''}
                </span>
              </StyledButton>
            </Col>
          </Row>
        </>
      )}
    </LoadingContainer>
  );
});
